import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from 'recharts';
import { Popup } from 'semantic-ui-react';
import styled from 'styled-components';
import { assertNotNil } from '../../../../utils/types';
import {
  ISignificanceTableData,
  ISignificanceVariantStats,
} from '../../interfaces';
import ResponsiveChartWrapper from '../../ResponsiveChartWrapper';
import { variantScatterColors } from './utils';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../../utils/formatting';
import { ChartMode } from '../../types';
import useCacheSignificanceLlmRawDataEffect from '../../hooks/llmCacheRawData/useCacheSignificanceLlmRawDataEffect';
import { Y_AXIS_TICK_CATEGORY_WIDTH } from '../../constants/utils';
import { theme } from '../../../../styles/theme';

interface IProps {
  data: ISignificanceTableData;
  analysisId: string;
  mode: ChartMode;
}

interface ISignificancePerformanceChartPoint {
  name: string;
  x: number;
}

interface ISignificancePerformanceScatter {
  data: ISignificancePerformanceChartPoint[];
  dotColor: string;
  lineColor: string;
}

const StyledPopup = styled(Popup)`
  &&& {
    display: inline-block;
    backdrop-filter: none;
    box-shadow: none;
    border: none;
    background: transparent;
    max-width: 250px;
    min-width: 30px;
    width: max-content;
    padding: 4px 8px;
    margin: 0;
    font-size: 12px;
    font-weight: 400;
    color: ${theme.colors.secondary900};
  }
`;

const getVariantScatter = (
  variant: ISignificanceVariantStats,
  name: string,
): ISignificancePerformanceScatter => {
  const { dotColor, lineColor } =
    variantScatterColors[variant.performanceResult];
  return {
    dotColor,
    lineColor,
    data: [
      {
        name,
        // manually multiply by 100 to convert to percentage to keep the correctness of
        // the data on visualization
        x: variant.confidenceInterval.lowerBound * 100,
      },
      {
        name,
        x: variant.lift * 100,
      },
      {
        name,
        x: variant.confidenceInterval.upperBound * 100,
      },
    ],
  };
};

const CustomScatterLine = (props: any) => {
  const [hovered, setHovered] = useState(false);
  const scatterData = props.scatterData as ISignificancePerformanceScatter;

  return (
    <g
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
    >
      <Line {...props} strokeWidth={6} stroke={scatterData.lineColor} />
      {scatterData.data.map((scatter, index) => {
        const circleCoordinates = props.points[index];
        assertNotNil(circleCoordinates);
        // Lift value has a bigger circle, therefore we have to make
        // some adjustments to popup
        const isLiftValue = index === 1;

        return (
          <StyledPopup
            key={`scatter-point-${index}`}
            open={hovered}
            hideOnScroll
            onClose={() => {
              if (hovered) {
                setHovered(false);
              }
            }}
            trigger={
              <circle
                cx={circleCoordinates.x}
                cy={circleCoordinates.y}
                r={isLiftValue ? 8 : 5}
                fill={scatterData.dotColor}
                stroke={scatterData.dotColor}
              />
            }
            content={
              <div>
                {globalFormat.formatNumber(
                  scatter.x,
                  defaultNumberFormatOptions.decimal,
                )}
                %
              </div>
            }
            position={isLiftValue ? 'bottom center' : 'top center'}
            offset={isLiftValue ? [5, 0] : []}
            pinned
            basic
          />
        );
      })}
    </g>
  );
};

const SignificancePerformanceChart = ({ data, analysisId, mode }: IProps) => {
  const { variantA, variantB } = data;
  assertNotNil(variantA);

  const scatterA = useMemo(
    () => getVariantScatter(variantA, 'Variant A'),
    [variantA],
  );
  const scatterB = useMemo(() => {
    if (!variantB) {
      return null;
    }
    return getVariantScatter(variantB, 'Variant B');
  }, [variantB]);

  // Cache the currently displayed "raw" data in redux, so that it can be used in AI requests "context" (prompts)
  useCacheSignificanceLlmRawDataEffect({ analysisId, data });

  return (
    <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
      <ResponsiveContainer>
        <ScatterChart layout="vertical" margin={{ top: 20, right: 20 }}>
          <CartesianGrid horizontal={false} />
          <XAxis
            type="number"
            dataKey="x"
            tickSize={3}
            domain={['dataMin - 2', 'dataMax + 2']}
            tick={({ x, y, payload: { value } }) => {
              return (
                <text
                  x={x}
                  y={y}
                  dy={16}
                  textAnchor="middle"
                  fill={theme.colors.secondary600}
                  fontSize={10}
                  fontWeight={400}
                >
                  {globalFormat.formatNumber(
                    value,
                    defaultNumberFormatOptions.decimal,
                  )}
                  %
                </text>
              );
            }}
          />
          <YAxis
            type="category"
            dataKey="name"
            tickSize={0}
            tickMargin={10}
            // constant width for Y-axis tick category since it's always the same
            // the tick category is either 'Variant A' or 'Variant B'
            width={Y_AXIS_TICK_CATEGORY_WIDTH}
            axisLine={false}
            // disable allowDuplicatedCategory to render variants as Y-axis value
            // all scatters for each variant will be connected with a single line
            allowDuplicatedCategory={false}
            tick={({ x, y, textAnchor, payload: { value } }) => (
              <text
                x={x}
                y={y}
                textAnchor={textAnchor}
                fill={theme.colors.secondary600}
                fontWeight={400}
                fontSize={10}
              >
                <tspan x={x} dy={'0.355em'}>
                  {value}
                </tspan>
              </text>
            )}
          />
          <Scatter
            data={scatterA.data}
            line={(props) => (
              <CustomScatterLine {...props} scatterData={scatterA} />
            )}
            shape={() => null}
          />
          {scatterB && (
            <Scatter
              data={scatterB.data}
              line={(props) => (
                <CustomScatterLine {...props} scatterData={scatterB} />
              )}
              shape={() => null}
            />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </ResponsiveChartWrapper>
  );
};

export default SignificancePerformanceChart;
